"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";

const BurnStatCards = ({ data, nodeRewardsStats, lastUpdated }) => {
  const [stats, setStats] = useState({
    totalBurned: 0,
    totalBurnedEcosystem: 0,
    totalBurnedNodeRewards: 0,
    supplyBurnPercent: 0,
    currentMaxSupply: 0,
    nodeRewardsBurnedAmount: 0,
    nodeRewardsCandidateAmount: 0,
    nodeRewardsBurnPercent: 0
  });
  
  const [formattedLastUpdate, setFormattedLastUpdate] = useState("Updated recently");

  useEffect(() => {
    if (lastUpdated) {
      // Format the date: e.g., "30 Jun 2025, 09:54 UTC"
      try {
        const date = new Date(lastUpdated);
        const options = { 
          day: '2-digit', 
          month: 'short', 
          year: 'numeric',
          hour: '2-digit', 
          minute: '2-digit',
          timeZone: 'UTC'
        };
        const formatted = date.toLocaleString('en-US', options) + ' UTC';
        setFormattedLastUpdate(`Last updated: ${formatted}`);
      } catch (e) {
        console.error("Date formatting error:", e);
        setFormattedLastUpdate("Updated recently");
      }
    }
  }, [lastUpdated]);

  useEffect(() => {
    if (data) {
      let nodeRewardsBurnedAmount = 0;
      let nodeRewardsCandidateAmount = 0;
      let nodeRewardsBurnPercent = 0;

      if (nodeRewardsStats) {
        nodeRewardsBurnedAmount = nodeRewardsStats.total_burned_from_events || 0;
        nodeRewardsCandidateAmount = nodeRewardsStats.total_candidate_amount || 0;
        
        if (nodeRewardsCandidateAmount > 0) {
          nodeRewardsBurnPercent = (nodeRewardsBurnedAmount / nodeRewardsCandidateAmount) * 100;
        }
      }

      setStats({
        totalBurned: data.total_burn_amt || 0,
        totalBurnedEcosystem: data.total_burn_amt_ecosystem || 0,
        totalBurnedNodeRewards: data.total_burn_amt_node_rewards || 0,
        supplyBurnPercent: data.supply_burn_percent || 0,
        currentMaxSupply: data.current_max_supply || 0,
        nodeRewardsBurnedAmount,
        nodeRewardsCandidateAmount,
        nodeRewardsBurnPercent
      });
    }
  }, [data, nodeRewardsStats]);

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toLocaleString();
  };

  const formatPercent = (percent) => {
    // Show at least 4 decimal places if the number is very small
    if (percent < 0.01) return percent.toFixed(4) + '%';
    return percent.toFixed(2) + '%';
  };

  const statCards = [
    {
      title: "Total Burned",
      value: formatNumber(stats.totalBurned),
      icon: "ph:fire-bold",
      gradient: "from-red-500 to-orange-500",
      bgColor: "bg-red-500/10",
      iconColor: "text-red-400"
    },
    {
      title: "Ecosystem Burns",
      value: formatNumber(stats.totalBurnedEcosystem),
      icon: "ph:tree-evergreen-bold",
      gradient: "from-green-500 to-emerald-500",
      bgColor: "bg-green-500/10",
      iconColor: "text-green-400"
    },
    {
      title: "Node Rewards Burns",
      value: formatNumber(stats.totalBurnedNodeRewards),
      icon: "ph:database-bold",
      gradient: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-500/10",
      iconColor: "text-blue-400",
      subtitle: nodeRewardsStats ? `${formatPercent(stats.nodeRewardsBurnPercent)} of candidate amount` : null,
      subtitleColor: stats.nodeRewardsBurnPercent >= 50 ? "text-green-400" : "text-red-400"
    },
    {
      title: "Supply Burn %",
      value: formatPercent(stats.supplyBurnPercent),
      icon: "ph:percent-bold",
      gradient: "from-purple-500 to-pink-500",
      bgColor: "bg-purple-500/10",
      iconColor: "text-purple-400"
    }
  ];

  if (!data) {
    return (
      <>
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
            <div className="animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-[#23272e] rounded-lg"></div>
                <div className="h-4 bg-[#23272e] rounded w-24"></div>
              </div>
              <div className="h-8 bg-[#23272e] rounded w-20 mb-2"></div>
            </div>
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      {statCards.map((card, index) => (
        <div key={index} className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6 hover:border-[#3a3f48] transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className={`h-10 w-10 ${card.bgColor} rounded-lg flex items-center justify-center`}>
              <Icon icon={card.icon} className={`text-xl ${card.iconColor}`} />
            </div>
            <h3 className="text-sm font-medium text-gray-400">{card.title}</h3>
          </div>
          <div className="space-y-2">
            <p className={`text-2xl font-bold bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent`}>
              {card.value}
            </p>
            {card.subtitle ? (
              <div className="flex items-center gap-1 text-xs">
                <Icon 
                  icon={stats.nodeRewardsBurnPercent >= 50 ? "ph:trend-up-bold" : "ph:trend-down-bold"} 
                  className={card.subtitleColor} 
                />
                <span className={card.subtitleColor}>{card.subtitle}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Icon icon="ph:clock-countdown" className="text-gray-400" />
                <span>{formattedLastUpdate}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default BurnStatCards; 